/* A single, optional scratch surface using the original printed poster artwork. */
(() => {
  if (customElements.get('ts-poster-preview')) return;
  class PosterPreview extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.stage = this.querySelector('[data-poster-stage]');
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas.getContext('2d');
      if (!this.context) return;
      this.image = this.querySelector('[data-poster-artwork]');
      this.cover = this.querySelector('[data-poster-cover]');
      this.button = this.querySelector('[data-poster-reveal]');
      this.status = this.querySelector('[data-poster-status]');
      this.levels = [...this.querySelectorAll('[data-poster-level]')];
      this.version = (this.version || 0) + 1;
      this.samples = new Map();
      this.sampler = document.createElement('canvas');
      this.sampler.width = this.sampler.height = 64;
      this.sampling = this.sampler.getContext('2d', { willReadFrequently: true });
      this.querySelector('[data-poster-interactive]').hidden = false;
      this.querySelector('[data-poster-fallback]').hidden = true;
      this.dataset.enhanced = '';
      this.levels.forEach(button => button.addEventListener('click', () => {
        if (button !== this.selected) this.select(button);
      }, options));
      this.button.addEventListener('click', () => {
        if (this.loading) return;
        if (this.revealed) this.reset();
        else this.reveal();
      }, options);
      this.canvas.addEventListener('pointerdown', event => {
        if (this.loading || this.revealed || !event.isPrimary || event.button !== 0) return;
        this.pointer = event.pointerId;
        this.canvas.setPointerCapture(event.pointerId);
        this.last = this.point(event);
        this.scratch(event);
      }, options);
      this.canvas.addEventListener('pointermove', event => {
        if (event.pointerId === this.pointer) this.scratch(event);
      }, options);
      const finish = event => {
        if (event.pointerId !== this.pointer) return;
        this.stopDrawing();
        this.checkCoverage();
      };
      this.canvas.addEventListener('pointerup', finish, options);
      this.canvas.addEventListener('pointercancel', finish, options);
      this.canvas.addEventListener('lostpointercapture', finish, options);
      this.resize = new ResizeObserver(() => {
        if (!this.loading) this.sizeCanvas(true);
      });
      this.resize.observe(this.stage);
      // Respect the server-selected deck and make it interactive on first paint.
      // The explicit aria-pressed check also keeps this resilient if the editor
      // reorders the level buttons or injects a different default later.
      this.select(this.levels.find(level => level.getAttribute('aria-pressed') === 'true') || this.levels[0]);
    }
    load(src) {
      if (!this.samples.has(src)) {
        this.samples.set(src, new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = src;
        }));
      }
      return this.samples.get(src);
    }
    async select(button) {
      const version = ++this.version;
      this.stopDrawing();
      this.selected = button;
      this.loading = true;
      this.revealed = false;
      this.stage.setAttribute('aria-busy', 'true');
      this.stage.removeAttribute('data-revealed');
      this.image.hidden = true;
      this.image.setAttribute('aria-hidden', 'true');
      this.canvas.hidden = true;
      this.cover.hidden = false;
      this.cover.src = button.dataset.foil;
      this.button.disabled = true;
      this.button.textContent = this.button.dataset.showLabel;
      this.button.setAttribute('aria-expanded', 'false');
      this.levels.forEach(level => level.setAttribute('aria-pressed', String(level === button)));
      this.querySelector('[data-poster-selected]').textContent = button.dataset.label;
      this.style.setProperty('--poster-marker-x', `${button.dataset.x}%`);
      this.style.setProperty('--poster-marker-y', `${button.dataset.y}%`);
      this.status.textContent = this.dataset.loadingLabel;
      try {
        const [art, foil] = await Promise.all([this.load(button.dataset.artwork), this.load(button.dataset.foil)]);
        if (version !== this.version || !this.isConnected) return;
        this.foil = foil;
        this.image.src = art.src;
        this.image.alt = button.dataset.description;
        await this.image.decode();
        if (version !== this.version || !this.isConnected) return;
        this.loading = false;
        this.image.hidden = false;
        this.reset();
      } catch {
        if (version !== this.version || !this.isConnected) return;
        this.querySelector('[data-poster-interactive]').hidden = true;
        this.querySelector('[data-poster-fallback]').hidden = false;
        this.removeAttribute('data-enhanced');
      }
    }
    sizeCanvas(preserve = false) {
      const width = Math.round(this.stage.getBoundingClientRect().width * Math.min(devicePixelRatio || 1, 2));
      if (!width || (preserve && this.canvas.width === width && this.canvas.height === width)) return;
      let previous;
      if (preserve && this.canvas.width && this.canvas.height) {
        previous = document.createElement('canvas');
        previous.width = this.canvas.width;
        previous.height = this.canvas.height;
        previous.getContext('2d').drawImage(this.canvas, 0, 0);
      }
      this.canvas.width = this.canvas.height = width;
      this.context.globalCompositeOperation = 'source-over';
      this.context.drawImage(previous || this.foil, 0, 0, width, width);
    }
    reset() {
      this.stopDrawing();
      this.revealed = false;
      this.sizeCanvas();
      this.canvas.hidden = false;
      this.cover.hidden = true;
      this.image.setAttribute('aria-hidden', 'true');
      this.stage.setAttribute('aria-busy', 'false');
      this.stage.removeAttribute('data-revealed');
      this.button.disabled = false;
      this.button.textContent = this.button.dataset.showLabel;
      this.button.setAttribute('aria-expanded', 'false');
      this.status.textContent = `${this.selected.dataset.label}. ${this.dataset.readyLabel}`;
    }
    reveal() {
      this.stopDrawing();
      this.revealed = true;
      this.canvas.hidden = true;
      this.image.removeAttribute('aria-hidden');
      this.stage.setAttribute('data-revealed', '');
      this.button.textContent = this.button.dataset.resetLabel;
      this.button.setAttribute('aria-expanded', 'true');
      this.status.textContent = `${this.selected.dataset.label}. ${this.dataset.revealedLabel}`;
    }
    point(event) {
      const rect = this.canvas.getBoundingClientRect();
      const scale = this.canvas.width / rect.width;
      return { x: (event.clientX - rect.left) * scale, y: (event.clientY - rect.top) * scale, scale };
    }
    scratch(event) {
      if (this.pointer === undefined || this.loading || this.revealed) return;
      const now = this.point(event);
      this.context.globalCompositeOperation = 'destination-out';
      this.context.lineWidth = 38 * now.scale;
      this.context.lineCap = 'round';
      this.context.beginPath();
      this.context.moveTo(this.last?.x ?? now.x, this.last?.y ?? now.y);
      this.context.lineTo(now.x + 0.01, now.y + 0.01);
      this.context.stroke();
      this.last = now;
    }
    stopDrawing() {
      const pointer = this.pointer;
      this.pointer = undefined;
      this.last = null;
      if (pointer !== undefined && this.canvas.hasPointerCapture(pointer)) this.canvas.releasePointerCapture(pointer);
    }
    checkCoverage() {
      if (this.revealed || this.loading || !this.sampling) return;
      // Read only a 64px sample on release, never the full retina canvas during a drag.
      this.sampling.clearRect(0, 0, 64, 64);
      this.sampling.drawImage(this.canvas, 0, 0, 64, 64);
      try {
        const pixels = this.sampling.getImageData(0, 0, 64, 64).data;
        let total = 0, cleared = 0;
        for (let y = 0; y < 64; y++) for (let x = 0; x < 64; x++) {
          if ((x - 31.5) ** 2 + (y - 31.5) ** 2 > 31 ** 2) continue;
          total++;
          if (pixels[(y * 64 + x) * 4 + 3] < 80) cleared++;
        }
        if (cleared / total > 0.38) this.reveal();
      } catch {
        // The reveal button remains available if a browser blocks pixel access.
      }
    }
    disconnectedCallback() {
      this.version++;
      this.stopDrawing();
      this.controller?.abort();
      this.resize?.disconnect();
    }
  }
  customElements.define('ts-poster-preview', PosterPreview);
})();
